import { useState } from "react";
import { Link } from "react-router-dom";

import { useCatalog } from "../../catalog/CatalogContext";
import OrderModal from "../../components/OrderModal/OrderModal";
import {
  CATEGORY_LABELS,
  Product,
  buildProductSlug,
} from "../../data/products";
import { slugify } from "../../utils/slugify";

type ProductsGridProps = {
  category: string;
  search?: string;
};

function matchesSearch(product: Product, search: string) {
  if (!search.trim()) return true;
  const needle = slugify(search);
  return slugify(product.title).includes(needle);
}

export default function ProductsGrid({ category, search = "" }: ProductsGridProps) {
  const { products, loading, error } = useCatalog();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const filteredProducts = products.filter((product) => {
    if (category === "shoes" && product.category !== "shoes") return false;
    if (category === "bags" && product.category !== "bags") return false;
    if (category === "news" && !product.isNew) return false;
    return matchesSearch(product, search);
  });

  return (
    <>
      {loading && (
        <div className="surface-panel rounded-[30px] px-6 py-14 text-center">
          <p className="brand-display text-3xl font-bold text-[#21140f]">
            Chargement du catalogue...
          </p>
        </div>
      )}

      {!loading && error && (
        <div className="surface-panel rounded-[30px] px-6 py-14 text-center">
          <p className="brand-display text-3xl font-bold text-[#21140f]">
            Catalogue indisponible
          </p>
          <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-[#7a6357]">
            {error}
          </p>
        </div>
      )}

      {!loading && !error && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {filteredProducts.length === 0 && (
            <div className="surface-panel col-span-full rounded-[30px] px-6 py-14 text-center">
              <p className="brand-display text-3xl font-bold text-[#21140f]">
                Aucun resultat trouve
              </p>
              <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-[#7a6357]">
                Essayez un autre mot-cle pour decouvrir davantage de modeles.
              </p>
            </div>
          )}

          {filteredProducts.map((product) => (
            <article key={product.id} className="surface-card rounded-[30px] p-4">
              <Link
                to={`/product/${buildProductSlug(product)}`}
                className="group block rounded-[24px] bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.94),rgba(239,223,208,0.92))] p-6"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="rounded-full bg-white/80 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8b634d]">
                    {CATEGORY_LABELS[product.category]}
                  </span>
                  {product.isNew && (
                    <span className="rounded-full bg-[#251713] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#fff0e3]">
                      New
                    </span>
                  )}
                </div>

                <div className="flex min-h-[290px] items-center justify-center py-8">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="max-h-[240px] max-w-full object-contain transition duration-700 group-hover:scale-105 group-hover:-rotate-2"
                  />
                </div>
              </Link>

              <div className="mt-5 flex items-start justify-between gap-4">
                <div>
                  <h3 className="brand-display text-2xl font-bold text-[#201410]">
                    {product.title}
                  </h3>
                  <p className="mt-2 max-w-xs text-sm leading-7 text-[#715c50]">
                    {product.description ??
                      "Une belle piece choisie pour ajouter du style a votre garde-robe."}
                  </p>
                </div>
                <span className="rounded-full bg-white/75 px-4 py-2 text-sm font-semibold text-[#201410]">
                  {product.price}
                </span>
              </div>

              <div className="mt-6 flex items-center justify-between gap-3">
                <Link
                  to={`/product/${buildProductSlug(product)}`}
                  className="cta-secondary rounded-full px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.26em] transition hover:bg-white"
                >
                  Voir plus
                </Link>
                <button
                  type="button"
                  onClick={() => setSelectedProduct(product)}
                  className="cta-primary rounded-full px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.26em] transition"
                >
                  Commander
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <OrderModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />
    </>
  );
}
